// useOnboardingGate — the canonical session + onboarding entry gate.
//
// Centralizes the decision that used to be split across EntryGate / AppEntry /
// Auth / AuthCallback / Processing. ONE indexed lookup (user_profiles.
// onboarding_complete by user_id), held behind a "pending" state so the wrong
// destination never flashes.
//
// Outcomes:
//   - session && onboarding_complete === true   -> authedTarget (voice-first: "/mirror")
//   - session && (no row yet OR != true)        -> incompleteTarget (default "/onboarding/identity")
//   - no session, returning user (vyv_has_authed)-> noSessionReturning
//   - no session, first visit                    -> noSessionFresh
//   - transient query error                      -> authedTarget (never bounce a
//                                                   completed user back to onboarding)
//   - getSession() failure                       -> null (caller decides: render
//                                                   children, or default a route)
//
// `enabled=false` makes the gate inert (returns null immediately) — used by
// EntryGate so it only runs on entry paths.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type OnboardingGateTarget = string | null | "pending";

export interface UseOnboardingGateOptions {
  authedTarget: string;
  incompleteTarget?: string;
  noSessionReturning: string | null;
  noSessionFresh: string | null;
  enabled?: boolean;
}

const ONBOARDING_ENTRY = "/onboarding/identity";

export function useOnboardingGate(
  opts: UseOnboardingGateOptions,
): OnboardingGateTarget {
  const {
    authedTarget,
    incompleteTarget = ONBOARDING_ENTRY,
    noSessionReturning,
    noSessionFresh,
    enabled = true,
  } = opts;

  const [target, setTarget] = useState<OnboardingGateTarget>(
    enabled ? "pending" : null,
  );

  useEffect(() => {
    if (!enabled) { setTarget(null); return; }
    let active = true;
    setTarget("pending");
    supabase.auth.getSession()
      .then(async ({ data }) => {
        if (!active) return;
        const session = data.session;
        if (!session) {
          const returning = !!localStorage.getItem("vyv_has_authed");
          setTarget(returning ? noSessionReturning : noSessionFresh);
          return;
        }
        const { data: profile, error } = await supabase
          .from("user_profiles")
          .select("onboarding_complete")
          .eq("user_id", session.user.id)
          .maybeSingle();
        if (!active) return;
        if (error) {
          console.error("[useOnboardingGate] onboarding_complete lookup failed:", error);
          setTarget(authedTarget);
          return;
        }
        setTarget(
          profile?.onboarding_complete === true ? authedTarget : incompleteTarget,
        );
      })
      .catch(() => { if (active) setTarget(null); });
    return () => { active = false; };
  }, [enabled, authedTarget, incompleteTarget, noSessionReturning, noSessionFresh]);

  return target;
}
